import type { Posting } from "@chotca/sdk";

// Health buckets drive the "Cần chú ý → Đang chạy → Đã đóng" grouping on
// the postings list. Logic is purely derived from existing posting fields,
// so the BE doesn't need to compute or persist a status column.
export type PostingHealth =
  | "stalled" // OPEN, posted ≥ STALLED_DAYS ago, 0 applicants — nudge the employer
  | "just_posted" // OPEN, < 24h since published — celebrate, no action needed yet
  | "filled" // OPEN and applicantStats.total ≥ headcount — implicit win
  | "healthy" // OPEN with at least one applicant or freshly active
  | "closed"; // any non-OPEN state — moves to bottom bucket

const STALLED_DAYS = 5;
const JUST_POSTED_HOURS = 24;

export function derivePostingHealth(p: Posting): PostingHealth {
  if (p.state !== "OPEN") return "closed";

  const publishedAt = p.publishedAt ?? p.createdAt;
  const ageMs = Date.now() - new Date(publishedAt).getTime();
  const applicants = p.applicantStats?.total ?? 0;

  if (ageMs < JUST_POSTED_HOURS * 3600 * 1000) return "just_posted";
  if (applicants >= p.headcount && p.headcount > 0) return "filled";
  if (applicants === 0 && ageMs >= STALLED_DAYS * 24 * 3600 * 1000)
    return "stalled";
  return "healthy";
}

// Buckets shown on the list, in display order. "filled" rolls into "healthy"
// visually because the per-card slot indicator already conveys it — splitting
// it out would add a one-card group that feels lonely.
export type HealthBucket = "needs_attention" | "running" | "closed";

export function bucketFromHealth(h: PostingHealth): HealthBucket {
  if (h === "stalled") return "needs_attention";
  if (h === "closed") return "closed";
  return "running";
}

// Days since the posting went live (or was created if never published).
export function daysSincePosted(p: Posting): number {
  const t = new Date(p.publishedAt ?? p.createdAt).getTime();
  return Math.floor((Date.now() - t) / (24 * 3600 * 1000));
}

// Days remaining until the posting expires. Negative if already expired.
// `null` when the posting has no expiry set (drafts, manually-managed tins).
// Postings live for 30 days by default — see Posting.expiresAt server logic.
export function daysUntilExpiry(p: Posting): number | null {
  if (!p.expiresAt) return null;
  const t = new Date(p.expiresAt).getTime();
  // Use ceil so "5h until expiry" reads as "Còn 1 ngày", not "0 ngày".
  return Math.ceil((t - Date.now()) / (24 * 3600 * 1000));
}
